import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminApi } from '@/api/client';
import DataTable from '@/components/common/DataTable';
import Modal from '@/components/common/Modal';
import ConfirmDialog from '@/components/common/ConfirmDialog';

interface Banner {
  id: string;
  title: string;
  imageUrl: string;
  linkUrl: string | null;
  order: number;
  isActive: boolean;
  createdAt: string;
}

export default function BannersPage() {
  const { t } = useTranslation();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState<Banner | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    imageUrl: '',
    linkUrl: '',
    order: 0,
    isActive: true,
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const response = await adminApi.getBanners();
      setBanners(response.data);
    } catch (err) {
      console.error('Error fetching banners:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateModal = () => {
    setSelectedBanner(null);
    setFormData({ title: '', imageUrl: '', linkUrl: '', order: 0, isActive: true });
    setIsModalOpen(true);
  };

  const openEditModal = (banner: Banner) => {
    setSelectedBanner(banner);
    setFormData({
      title: banner.title,
      imageUrl: banner.imageUrl,
      linkUrl: banner.linkUrl || '',
      order: banner.order,
      isActive: banner.isActive,
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const data = {
        ...formData,
        linkUrl: formData.linkUrl || null,
      };

      if (selectedBanner) {
        await adminApi.updateBanner(selectedBanner.id, data);
      } else {
        await adminApi.createBanner(data);
      }
      setIsModalOpen(false);
      fetchBanners();
    } catch (err) {
      console.error('Error saving banner:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedBanner) return;
    setIsSaving(true);

    try {
      await adminApi.deleteBanner(selectedBanner.id);
      setIsDeleteDialogOpen(false);
      setSelectedBanner(null);
      fetchBanners();
    } catch (err) {
      console.error('Error deleting banner:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleActive = async (banner: Banner) => {
    try {
      await adminApi.updateBanner(banner.id, { isActive: !banner.isActive });
      fetchBanners();
    } catch (err) {
      console.error('Error toggling banner:', err);
    }
  };

  const columns = [
    {
      key: 'image',
      label: 'Preview',
      render: (banner: Banner) => (
        <img src={banner.imageUrl} alt={banner.title} className="w-20 h-12 object-cover rounded" />
      ),
    },
    { key: 'title', label: t('banners.bannerTitle') },
    { key: 'order', label: t('banners.order') },
    {
      key: 'status',
      label: t('banners.status'),
      render: (banner: Banner) => (
        <button
          onClick={() => toggleActive(banner)}
          className={`px-2 py-1 text-xs rounded-full ${banner.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}
        >
          {banner.isActive ? t('banners.active') : t('banners.inactive')}
        </button>
      ),
    },
    {
      key: 'actions',
      label: t('common.actions'),
      render: (banner: Banner) => (
        <div className="flex gap-2">
          <button onClick={() => openEditModal(banner)} className="p-1 text-gray-500 hover:text-primary">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => { setSelectedBanner(banner); setIsDeleteDialogOpen(true); }}
            className="p-1 text-gray-500 hover:text-red-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('banners.title')}</h1>
        <button onClick={openCreateModal} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark text-sm sm:text-base whitespace-nowrap">
          {t('banners.addBanner')}
        </button>
      </div>

      <DataTable columns={columns} data={banners} isLoading={isLoading} keyExtractor={(banner) => banner.id} />

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedBanner ? t('banners.editBanner') : t('banners.addBanner')}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('banners.bannerTitle')}</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('banners.imageUrl')}</label>
            <input
              type="url"
              value={formData.imageUrl}
              onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            />
            {formData.imageUrl && (
              <img src={formData.imageUrl} alt="Preview" className="mt-2 w-full h-32 object-cover rounded" />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('banners.linkUrl')}</label>
            <input
              type="url"
              value={formData.linkUrl}
              onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('banners.order')}</label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: Number(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              min="0"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="w-4 h-4 text-primary"
            />
            <label htmlFor="isActive" className="text-sm font-medium text-gray-700">{t('banners.active')}</label>
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={isSaving} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50">
              {isSaving ? 'Saving...' : t('common.save')}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title={t('banners.deleteBanner')}
        message={t('banners.deleteConfirm')}
        isLoading={isSaving}
      />
    </div>
  );
}
